import { Component, Inject, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormGroupDirective, UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { County } from 'src/app/model/county';
import { Region } from 'src/app/model/region.model';
import { Grade, School } from 'src/app/model/school.model';
import { SchoolDistrict } from 'src/app/model/schoolDistrict.model';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import Swal from 'sweetalert2';

@Component({
    selector: 'resolve-promote-enrollment-details-modal',
    templateUrl: './resolve-promote-enrollment-details-modal.component.html',
    styleUrls: ['./resolve-promote-enrollment-details-modal.component.scss']
})
export class ResolvePromoteEnrollmentDetailsModalComponent implements OnInit {
    @Input() schoolEnrollment!: any;
    @Input() uniqueStudents!: any;

    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    @ViewChild('enrollmentForm') childInfoForm: FormGroupDirective;
    form: UntypedFormGroup;

    // Table results
    tableDataSources: MatTableDataSource<any>[] = [];
    // Table display
    displayedColumns = ['EDIT', 'MSD_NUMBER', 'NAME'];

    selectedSchoolYear: string = '';
    initialSchoolYear: string = '';
    currentSchoolYear: string = '';
    chosenRegion?: Region;
    chosenCounty?: County;
    chosenSchoolDistrict?: SchoolDistrict;
    chosenSchool?: School;
    chosenGrades: Grade[] = [];

    selectRegionIds: number[] = [];
    selectCountyIds: number[] = [];
    selectSchoolDistrictIds: number[] = [];
    selectSchoolIds: number[] = [];
    selectSchoolYear: string;

    enrollmentTypes: any[] = [
        { value: 'R', display: 'R - Regular Term Enrollment', disabled: false },
        { value: 'N', display: 'N - Non-attending', disabled: false }
    ];

    isLoading: boolean = false;
    msg: MsgModel;

    // Pipes
    cleanDatePipe: CleanDatePipe = new CleanDatePipe();
    getPropertyPipe: GetPropertyPipe = new GetPropertyPipe();

    constructor(
        public dialogRef: MatDialogRef<ResolvePromoteEnrollmentDetailsModalComponent>,
        private dialog: MatDialog,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private fb: UntypedFormBuilder
    ) {
        this.form = fb.group({
            enrollmentType: ['', [Validators.required]],
            enrollmentDate: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.isLoading = true;

        // Use data IDs if exists
        if (this.data) {
            this.schoolEnrollment = this.data.schoolEnrollment;
            this.uniqueStudents = this.data.uniqueStudents;
            this.selectRegionIds.push(this.data.location.REGIONS_ID);
            this.selectCountyIds.push(this.data.location.COUNTIES_ID);
            this.selectSchoolDistrictIds.push(this.data.location.SCHOOL_DISTRICTS_ID);
            this.selectSchoolIds.push(this.data.location.SCHOOLS_ID);

            let sed = this.schoolEnrollment.schoolEnrollmentDetails;

            this.form.patchValue({
                enrollmentType: sed['EnrollmentType'],
                enrollmentDate: this.cleanDatePipe.transform(sed['EnrollmentDate'])
            });
            this.enrollmentTypeChange({ value: sed['EnrollmentType'] });

            if (sed['SchoolYear']) {
                this.selectSchoolYear = sed['SchoolYear'];
                this.selectedSchoolYear = sed['SchoolYear'];
            }
            if (sed['Grade']) {
                let newGrade = new Grade();
                newGrade.GRADES = sed['Grade'];
                this.chosenGrades.push(newGrade);
            }

            this.updateDataSource();
        }

        this.isLoading = false;
    }

    // Location Emitters
    regionEventEmitter(regions: Region[]) {
        this.chosenRegion = regions ? regions[0] : undefined;
    }

    countiesEventEmitter(counties: County[]) {
        this.chosenCounty = counties ? counties[0] : undefined;
    }

    districtsEventEmitter(districts: SchoolDistrict[]) {
        this.chosenSchoolDistrict = districts ? districts[0] : undefined;
    }

    schoolsEventEmitter(schools: School[]) {
        this.chosenSchool = schools ? schools[0] : undefined;
    }

    performancePeriodEvent(performancePeriod: string) {
        this.selectedSchoolYear = performancePeriod;
    }

    enrollmentTypeChange(event: any) {
        if (event.value === 'R') {
            this.form.get('enrollmentDate')?.enable();

            this.form.get('enrollmentDate')?.setValidators([Validators.required]);
            this.form.get('enrollmentDate')?.updateValueAndValidity();
        } else if (event.value === 'N') {
            this.form.get('enrollmentDate')?.disable();
            this.form.patchValue({
                enrollmentDate: '',
                withdrawalDate: ''
            });

            this.form.get('enrollmentDate')?.setValidators([]);
            this.form.get('enrollmentDate')?.updateValueAndValidity();
        }
    }

    schoolYearEvent(schoolYear: string) {
        this.selectedSchoolYear = schoolYear;

        this.setDates(schoolYear);
    }

    setDates(schoolYear: string) {
        this.setEnrollmentDate(schoolYear);
    }

    setEnrollmentDate(schoolYear: string) {
        if (schoolYear) {
            let minYear = schoolYear.substring(0, 4);
            let maxYear = schoolYear.substring(0, 2) + schoolYear.substring(5, 7);

            this.schoolEnrollment.enrollmentDateOptions = {
                datepickerMode: 'year',
                minDate: new Date('07/01/"' + minYear + '"')
            };

            if (this.schoolEnrollment.lowestEligibleStudent) {
                if (
                    this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(
                            this.schoolEnrollment,
                            'lowestEligibleStudent.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                        )
                    ) >= new Date('06/30/"' + maxYear + '"')
                ) {
                    this.schoolEnrollment.hasEnrollmentDateRestriction = false;
                    this.schoolEnrollment.enrollmentDateOptions.maxDate = new Date('06/30/"' + maxYear + '"');
                } else {
                    this.schoolEnrollment.hasEnrollmentDateRestriction = true;
                    this.schoolEnrollment.enrollmentDateOptions.maxDate = this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(
                            this.schoolEnrollment,
                            'lowestEligibleStudent.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                        )
                    );
                }

                if (
                    this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(
                            this.schoolEnrollment,
                            'lowestEligibleStudent.SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                        )
                    ) < this.schoolEnrollment.enrollmentDateOptions.minDate
                ) {
                    this.schoolEnrollment.endOfEligibilityError = true;
                } else {
                    this.schoolEnrollment.endOfEligibilityError = false;
                }
            } else {
                this.schoolEnrollment.hasEnrollmentDateRestriction = false;
                this.schoolEnrollment.enrollmentDateOptions.maxDate = new Date('06/30/"' + maxYear + '"');
            }
        } else {
            this.schoolEnrollment.hasEnrollmentDateRestriction = false;
            this.schoolEnrollment.enrollmentDateOptions = {
                datepickerMode: 'year',
                minDate: null,
                maxDate: null
            };
        }

        this.updateDates();
    }

    updateDates() {
        if (
            this.cleanDatePipe.transform(this.form.get('enrollmentDate')?.value) <
            this.cleanDatePipe.transform(this.schoolEnrollment.enrollmentDateOptions.minDate)
        ) {
            this.form.patchValue({
                enrollmentDate: ''
            });
        } else if (
            this.cleanDatePipe.transform(this.form.get('enrollmentDate')?.value) >
            this.cleanDatePipe.transform(this.schoolEnrollment.enrollmentDateOptions.maxDate)
        ) {
            this.form.patchValue({
                enrollmentDate: ''
            });
        }
    }

    gradeEventEmitter(grades: Grade[]) {}

    setAndFindLowestEligibleStudent(schoolEnrollment: any) {
        schoolEnrollment.lowestEligibleStudent = undefined;

        schoolEnrollment.STUDENTS.forEach((s: any) => {
            if (s.ENABLED) {
                let student = this.uniqueStudents.find((us: any) => us.ID === s.ID);

                let eoeDate = this.cleanDatePipe.transform(
                    this.getPropertyPipe.transform(student, 'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE')
                );

                if (eoeDate) {
                    if (!schoolEnrollment.lowestEligibleStudent) {
                        schoolEnrollment.lowestEligibleStudent = student;
                    } else {
                        let currentEoE = this.cleanDatePipe.transform(
                            this.getPropertyPipe.transform(
                                schoolEnrollment.lowestEligibleStudent,
                                'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                            )
                        );

                        if (new Date(eoeDate) < new Date(currentEoE)) {
                            schoolEnrollment.lowestEligibleStudent = student;
                        }
                    }
                }
            }
        });
    }

    resolveLowestEligibleStudent(schoolEnrollment: any) {
        if (this.getPropertyPipe.transform(schoolEnrollment, 'schoolEnrollmentDetails.SchoolYear')) {
            let minYear = schoolEnrollment.schoolEnrollmentDetails.SchoolYear.substring(0, 4);
            let maxYear =
                schoolEnrollment.schoolEnrollmentDetails.SchoolYear.substring(0, 2) + schoolEnrollment.schoolEnrollmentDetails.SchoolYear.substring(5, 7);

            schoolEnrollment.enrollmentDateOptions = {
                datepickerMode: 'year',
                minDate: new Date('07/01/"' + minYear + '"'),
                maxDate: new Date('06/30/"' + maxYear + '"')
            };

            if (schoolEnrollment.lowestEligibleStudent) {
                if (
                    this.cleanDatePipe.transform(
                        this.getPropertyPipe.transform(
                            schoolEnrollment.lowestEligibleStudent,
                            'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                        )
                    ) >= new Date('06/30/"' + maxYear + '"')
                ) {
                    schoolEnrollment.hasEnrollmentDateRestriction = false;
                    schoolEnrollment.enrollmentDateOptions.maxDate = new Date('06/30/"' + maxYear + '"');
                } else {
                    schoolEnrollment.hasEnrollmentDateRestriction = true;
                    schoolEnrollment.enrollmentDateOptions.maxDate = new Date(
                        this.cleanDatePipe.transform(
                            this.getPropertyPipe.transform(
                                schoolEnrollment.lowestEligibleStudent,
                                'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                            )
                        )
                    );
                    if (schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate >= schoolEnrollment.enrollmentDateOptions.maxDate) {
                        schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate = schoolEnrollment.enrollmentDateOptions.maxDate;
                    }
                }

                if (
                    new Date(
                        this.cleanDatePipe.transform(
                            this.getPropertyPipe.transform(
                                schoolEnrollment.lowestEligibleStudent,
                                'SCHOOL_ENROLLMENT_DETAILS[0].STUDENT_ELIGIBILITIES[0].ELIGIBILITY_END_DATE'
                            )
                        )
                    ) < schoolEnrollment.enrollmentDateOptions.minDate
                ) {
                    schoolEnrollment.endOfEligibilityError = true;
                    schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate = '';
                } else {
                    schoolEnrollment.endOfEligibilityError = false;
                }
            } else {
                schoolEnrollment.hasEnrollmentDateRestriction = false;
                schoolEnrollment.enrollmentDateOptions.maxDate = new Date('06/30/"' + maxYear + '"');
            }

            if (
                schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate < schoolEnrollment.enrollmentDateOptions.minDate &&
                !schoolEnrollment.endOfEligibilityError
            ) {
                schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate = schoolEnrollment.enrollmentDateOptions.minDate;
            }
        }
    }

    toggleAllStudents(schoolEnrollment: any) {
        if (this.hasAtLeastOneStudent(schoolEnrollment)) {
            schoolEnrollment.STUDENTS.forEach((student: any) => {
                let promotingStudent = this.schoolEnrollment.STUDENTS.find((ps: any) => {
                    return ps.ID === student.ID;
                });
                promotingStudent.ENABLED = false;
            });
        } else {
            schoolEnrollment.STUDENTS.forEach((student: any) => {
                let promotingStudent = this.schoolEnrollment.STUDENTS.find((ps: any) => {
                    return ps.ID === student.ID;
                });
                promotingStudent.ENABLED = true;
            });
        }

        this.setAndFindLowestEligibleStudent(schoolEnrollment);
        this.resolveLowestEligibleStudent(schoolEnrollment);
        this.setDates(this.selectedSchoolYear);

        console.warn(schoolEnrollment);
    }

    toggleStudent(student: any, schoolEnrollment: any) {
        let promotingStudent = this.schoolEnrollment.STUDENTS.find((ps: any) => {
            return ps.ID === student.ID;
        });
        promotingStudent.ENABLED = !promotingStudent.ENABLED;

        this.setAndFindLowestEligibleStudent(schoolEnrollment);
        this.resolveLowestEligibleStudent(schoolEnrollment);
        this.setDates(this.selectedSchoolYear);

        console.warn(student);
        console.warn(schoolEnrollment);
        console.warn(promotingStudent);
    }

    hasAtLeastOneStudent(schoolEnrollment: any) {
        let found = false;

        found = schoolEnrollment.STUDENTS.find((student: any) => {
            if (student.ENABLED) {
                return (found = true);
            } else {
                return (found = false);
            }
        });

        return !!found;
    }

    getEnabledStudentsCount(students: any[]) {
        if (students) {
            return students.filter((student) => {
                return student.ENABLED;
            }).length;
        } else return 0;
    }

    /****************************************************************************************
		Table Functions
	****************************************************************************************/
    updateDataSource(newSort?: boolean) {
        setTimeout(() => {
            this.tableDataSources = [];

            let dataSource = new MatTableDataSource<any>();
            dataSource.data = this.schoolEnrollment.STUDENTS.sort((n1: any, n2: any) => (n1.NAME! > n2.NAME! ? 1 : -1));

            // Paginator
            dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            // dataSource.sort = this.sorts.toArray()[index];
            // dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);
            // if (newSort) {
            //     const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            //     dataSource.sort.active = defaultSort.active;
            //     dataSource.sort.direction = defaultSort.direction;
            //     dataSource.sort.sortChange.emit(defaultSort);
            // } else {
            //     dataSource.sort.sortChange.emit(this.sorts.toArray()[index]);
            // }

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);
        }, 100);
    }

    /*
     * SUBMIT functions
     */
    submit() {
        if (this.checkValid()) {
            this.msg = new MsgModel();

            //let payload = this.generatePayload();
            //console.warn(payload);

            if (this.schoolEnrollment) {
                //this.schoolEnrollment.STUDENTS = this.schoolEnrollment.STUDENTS.filter((s: any) => s.ENABLED);

                let location = {
                    REGION: this.chosenRegion,
                    COUNTy: this.chosenCounty,
                    SCHOOL_DISTRICTS: this.chosenSchoolDistrict,
                    SCHOOL: this.chosenSchool
                };

                this.schoolEnrollment.schoolEnrollmentDetails.EnrollmentType = this.form.get('enrollmentType')?.value;
                this.schoolEnrollment.schoolEnrollmentDetails.SchoolYear = this.selectedSchoolYear;
                this.schoolEnrollment.schoolEnrollmentDetails.Grade = this.chosenGrades[0] ? this.chosenGrades[0].GRADES : '';
                this.schoolEnrollment.schoolEnrollmentDetails.EnrollmentDate = this.form.get('enrollmentDate')?.value;
                console.warn({ newSchoolEnrollment: this.schoolEnrollment, location: location });
                setTimeout(() => {
                    this.dialogRef.close({ newSchoolEnrollment: this.schoolEnrollment, location: location });
                }, 500);
            } else {
            }
        }
    }

    checkValid() {
        this.form.markAllAsTouched();
        let valid = true;
        if (!this.chosenRegion?.ID) {
            this.msg = { MSG: 'Please select a Region', SUCCESS: false };
            valid = false;
        } else if (!this.chosenCounty?.ID) {
            this.msg = { MSG: 'Please select a County', SUCCESS: false };
            valid = false;
        } else if (!this.chosenSchoolDistrict?.ID) {
            this.msg = { MSG: 'Please select a School District', SUCCESS: false };
            valid = false;
        } else if (!this.chosenSchool?.ID) {
            this.msg = { MSG: 'Please select a School', SUCCESS: false };
            valid = false;
        } else if (!this.chosenGrades || this.chosenGrades.length === 0) {
            this.msg = { MSG: 'Please select a Grade', SUCCESS: false };
            valid = false;
        } else if (!this.selectedSchoolYear) {
            this.msg = { MSG: 'Please select a School Year', SUCCESS: false };
            valid = false;
        } else if (this.form.invalid) {
            this.msg = { MSG: 'Please input required fields', SUCCESS: false };
            valid = false;
        } else if (!this.hasAtLeastOneStudent(this.schoolEnrollment)) {
            this.msg = { MSG: 'Please select at least one child', SUCCESS: false };
            valid = false;
        }

        setTimeout(() => {
            this.msg = undefined!;
        }, 7000);

        return valid;
    }

    // Dialog function
    closeDialog(closed: boolean) {
        Swal.fire({
            titleText: 'Are you sure you want to continue without saving?',
            showDenyButton: false,
            showConfirmButton: true,
            showCancelButton: true,
            confirmButtonText: 'Continue',
            cancelButtonText: 'Go Back',
            icon: 'warning',
            confirmButtonColor: '#3a6b35'
        }).then((result: any) => {
            if (result.isConfirmed) {
                this.dialogRef.close();
            }
        });
    }
}
